import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	distinctUntilChanged,
	map,
	switchMap,
	withLatestFrom,
	filter,
	of,
	merge,
} from 'rxjs';

import {
	fetchGuides,
	fetchCareerGuides,
	fetchCityGuides,
	fetchDegreeGuides,
	loadStateGuidesFailure,
	updateGuides,
	updateCareerGuides,
	updateCityGuides,
	updateDegreeGuides,
	fetchSingleGuide,
	updateSingleGuide,
} from './guides.actions';

import {
	getStatePopularDegreeGuides,
	getStateGuides,
	getStatePopularCityGuides,
	getStatePopularCareerGuides,
} from './guides.selectors';
import { GuidesApiService } from '@uc/web/shared/data-access';

@Injectable()
export class StateGuidesEffects {
	constructor(
		private readonly _actions$: Actions,
		private _store: Store,
		private _guidesApiSrv: GuidesApiService,
	) {}

	fetchGuides = createEffect(() =>
		this._actions$.pipe(
			ofType(fetchGuides),
			distinctUntilChanged(),
			withLatestFrom(this._store.select(getStateGuides)),
			filter(([, guides]) => !guides),
			switchMap(() => this._guidesApiSrv.getGuides()),
			map((response) => updateGuides(response)),
			catchError(async (error) => loadStateGuidesFailure(error)),
		),
	);

	fetchDegreeGuides = createEffect(() =>
		this._actions$.pipe(
			ofType(fetchDegreeGuides),
			distinctUntilChanged(),
			withLatestFrom(this._store.select(getStatePopularDegreeGuides)),
			filter(([, popularDegreeGuides]) => !popularDegreeGuides),
			switchMap(() => this._guidesApiSrv.getDegreeGuides()),
			map((response) => updateDegreeGuides(response)),
			catchError(async (error) => loadStateGuidesFailure(error)),
		),
	);

	fetchCityGuides = createEffect(() =>
		this._actions$.pipe(
			ofType(fetchCityGuides),
			distinctUntilChanged(),
			withLatestFrom(this._store.select(getStatePopularCityGuides)),
			filter(([, cityGuides]) => !cityGuides),
			switchMap(() => this._guidesApiSrv.getCityGuides()),
			map((response) => updateCityGuides(response)),
			catchError(async (error) => loadStateGuidesFailure(error)),
		),
	);

	fetchCareerGuides = createEffect(() =>
		this._actions$.pipe(
			ofType(fetchCareerGuides),
			distinctUntilChanged(),
			withLatestFrom(this._store.select(getStatePopularCareerGuides)),
			filter(([, careerGuides]) => !careerGuides),
			switchMap(() => this._guidesApiSrv.getCareerGuides()),
			map((response) => updateCareerGuides(response)),
			catchError(async (error) => loadStateGuidesFailure(error)),
		),
	);

	fetchSingleGuide = createEffect(() =>
		this._actions$.pipe(
			ofType(fetchSingleGuide),
			distinctUntilChanged(),
			switchMap((payload) => {
				const { guideName, categoryId } = payload;

				return merge(
					of(null),
					this._guidesApiSrv.getIndividualGuide(guideName, categoryId),
				);
			}),
			map((response) => updateSingleGuide({ singleGuide: response })),
			catchError((error) => of(loadStateGuidesFailure(error))),
		),
	);
}
